import { useCallback, useEffect, useMemo, useState } from 'react';
import useHttp from '../../../../hooks/use-http';
import { currencyFormatter } from '../../../../functions/formatters';
import i18n from '../../../../i18n';
import Icon from '../../../../icons/Icon';
import FormBlock from '../../../../components/Form/FormBlock';

const ORDER_API = '/rest/salesorder/current/';
// Store selection / order form block. This is the only place we POST from this page.
const ORDER_FORM = '/rest/salesorder/current/add_options/';

const formatLineQuantity = (value) => {
    const n = Number.parseFloat(value);
    if (Number.isNaN(n)) return '—';
    return Number.isInteger(n) ? String(n) : String(n);
};

const getLineDisplayName = (line, productObj) => {
    return (
        line.description ||
        productObj?.name ||
        productObj?.product_number ||
        i18n.t('Product')
    );
};

function getPrimaryProductImage(productObj, lineTitle) {
    const imgs = productObj?.productimages_objects || [];
    if (!Array.isArray(imgs) || imgs.length === 0) return null;

    const first = [...imgs].sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))[0];
    return {
        src: first?.image_webp || first?.image || null,
        alt: first?.alt_text || lineTitle || productObj?.name || i18n.t('Product'),
    };
}


const LINE_THUMB = 58;

const strNorm = (v) => (v == null ? '' : String(v).trim());

/** Snapshot for display/compare; country prefers nested object name. */
function addressSnapshotFromOrder(order, billing) {
    if (billing) {
        return {
            company: strNorm(order.billing_company),
            first: strNorm(order.billing_first_name),
            last: strNorm(order.billing_last_name),
            email: strNorm(order.billing_email),
            phone: strNorm(order.billing_phone),
            street: strNorm(order.billing_street),
            number: strNorm(order.billing_number),
            city: strNorm(order.billing_city),
            state: strNorm(order.billing_state),
            postal: strNorm(order.billing_postal_code),
            country:
                strNorm(order.billing_country_object?.name) || strNorm(order.billing_country),
        };
    }
    return {
        company: strNorm(order.company ?? order.delivery_company),
        first: strNorm(order.first_name),
        last: strNorm(order.last_name),
        email: strNorm(order.email),
        phone: strNorm(order.phone),
        street: strNorm(order.street),
        number: strNorm(order.number),
        city: strNorm(order.city),
        state: strNorm(order.state),
        postal: strNorm(order.postal_code),
        country: strNorm(order.country_object?.name) || strNorm(order.country),
    };
}

function snapshotsEqual(a, b) {
    const keys = [
        'company',
        'first',
        'last',
        'email',
        'phone',
        'street',
        'number',
        'city',
        'state',
        'postal',
        'country',
    ];
    return keys.every((k) => a[k] === b[k]);
}

/** True when billing has no contact/address text (empty billing = same as delivery). */
function hasBillingTextOverride(order) {
    const b = addressSnapshotFromOrder(order, true);
    return !!(
        b.company ||
        b.first ||
        b.last ||
        b.email ||
        b.phone ||
        b.street ||
        b.number ||
        b.city ||
        b.state ||
        b.postal
    );
}

function snapshotHasAnyContent(s) {
    return Object.values(s).some(Boolean);
}

function OrderAddressCard({ title, snapshot }) {
    const nameLine = [snapshot.first, snapshot.last].filter(Boolean).join(' ');
    const streetLine = [snapshot.street, snapshot.number].filter(Boolean).join(' ');
    const cityLine = [snapshot.postal, snapshot.city].filter(Boolean).join(' ');
    const hasContact = !!(snapshot.email || snapshot.phone);

    return (
        <div className="border rounded-3 p-3 h-100 bg-white border-secondary-subtle">
            <div
                className="text-muted text-uppercase fw-bold mb-2"
                style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}
            >
                {title}
            </div>
            <div className="text-body" style={{ lineHeight: 1.45 }}>
                {snapshot.company ? <div className="fw-semibold">{snapshot.company}</div> : null}
                {nameLine ? <div className="fw-semibold">{nameLine}</div> : null}
                {streetLine ? <div>{streetLine}</div> : null}
                {cityLine ? <div>{cityLine}</div> : null}
                {snapshot.state ? <div>{snapshot.state}</div> : null}
                {snapshot.country ? <div className="mt-1">{snapshot.country}</div> : null}
                {hasContact ? (
                    <div className="mt-2 pt-2 border-top border-secondary-subtle small">
                        {snapshot.email ? (
                            <div className="mb-1">
                                <span className="text-muted">{i18n.t('Email')}: </span>
                                <span>{snapshot.email}</span>
                            </div>
                        ) : null}
                        {snapshot.phone ? (
                            <div className="mb-0">
                                <span className="text-muted">{i18n.t('Phone')}: </span>
                                <span>{snapshot.phone}</span>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function OrderLineRow({ line }) {
    const productObj = line.product_listing?.product_object || line.product_object || line.product || {};
    const displayName = getLineDisplayName(line, productObj);
    const primaryImage = getPrimaryProductImage(productObj, displayName);
    const quantity = Number.parseFloat(line.quantity ?? 1);
    const qtySafe = Number.isNaN(quantity) ? 1 : quantity;
    const unitAmount =
        parseFloat(line.unit_amount_in_vat ?? line.unit_amount ?? line.unit_price ?? 0) || 0;
    const lineTotal =
        parseFloat(
            line.amount_in_vat ?? line.total_amount ?? line.amount ?? unitAmount * qtySafe,
        ) || 0;

    const TitleTag = productObj.url ? 'a' : 'span';
    const titleProps = productObj.url
        ? {
              href: productObj.url,
              className: 'text-decoration-none text-dark fw-semibold small',
          }
        : { className: 'fw-semibold text-dark small' };

    const hasLineErrors = Array.isArray(line.line_errors) && line.line_errors.length > 0;

    const thumb = primaryImage?.src ? (
        productObj.url ? (
            <a href={productObj.url} className="d-inline-block flex-shrink-0">
                <img
                    src={primaryImage.src}
                    alt={primaryImage.alt}
                    width={LINE_THUMB}
                    height={LINE_THUMB}
                    loading="lazy"
                    className="rounded border bg-white"
                    style={{ objectFit: 'contain' }}
                />
            </a>
        ) : (
            <span className="d-inline-block flex-shrink-0">
                <img
                    src={primaryImage.src}
                    alt={primaryImage.alt}
                    width={LINE_THUMB}
                    height={LINE_THUMB}
                    loading="lazy"
                    className="rounded border bg-white"
                    style={{ objectFit: 'contain' }}
                />
            </span>
        )
    ) : (
        <div
            className="rounded border bg-light flex-shrink-0"
            style={{ width: LINE_THUMB, height: LINE_THUMB }}
            aria-hidden="true"
        />
    );

    return (
        <div className="border-bottom py-2">
            <div className="d-flex gap-2 align-items-start">
                {thumb}
                <div className="flex-grow-1 min-w-0 w-100">
                    <div className="d-flex justify-content-between align-items-baseline gap-2">
                        <TitleTag {...titleProps}>{displayName}</TitleTag>
                        <span className="small fw-semibold text-nowrap flex-shrink-0">
                            {currencyFormatter(lineTotal)}
                        </span>
                    </div>
                    {hasLineErrors && (
                        <ul
                            className="text-danger mb-0 mt-1 ps-3 w-100"
                            style={{ fontSize: '0.75rem', listStylePosition: 'outside' }}
                        >
                            {line.line_errors.map((msg, idx) => (
                                <li key={idx}>{msg}</li>
                            ))}
                        </ul>
                    )}
                    {line.order_line_objects?.length > 0 && (
                        <div
                            className="text-muted mt-1 ps-2 border-start border-2 border-secondary-subtle"
                            style={{ fontSize: '0.65rem' }}
                        >
                            {line.order_line_objects.map((ol) => {
                                const olProduct = ol.product_object || ol.product || {};
                                const olName =
                                    ol.description ||
                                    ol.variant_name ||
                                    olProduct?.name ||
                                    olProduct?.product_number ||
                                    i18n.t('Product');
                                const olQty = Number.parseFloat(ol.quantity ?? 1);
                                return (
                                    <div key={ol.id}>
                                        {olName} × {formatLineQuantity(olQty)}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const LINES_PANEL_MQ = '(min-width: 768px)';

/**
 * Product panel: collapsible on all viewports.
 * Default: expanded on desktop (≥768px), collapsed on smaller screens — updated on resize.
 */
function useOrderLinesPanelOpen() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const mql = window.matchMedia(LINES_PANEL_MQ);
        const sync = () => setOpen(mql.matches);
        sync();
        mql.addEventListener('change', sync);
        return () => mql.removeEventListener('change', sync);
    }, []);

    const toggle = useCallback(() => setOpen((o) => !o), []);

    return { open, toggle };
}

function OrderOverviewSkeleton() {
    const SkeletonLine = () => (
        <div className="d-flex gap-2 border-bottom py-2">
            <span className="placeholder rounded bg-secondary-subtle flex-shrink-0" style={{ width: 48, height: 48 }} />
            <div className="placeholder-glow flex-grow-1">
                <div className="placeholder rounded-pill col-8 mb-1" style={{ height: 10 }}></div>
                <div className="placeholder rounded-pill col-5" style={{ height: 8 }}></div>
            </div>
        </div>
    );

    return (
        <div className="row g-3 g-lg-4">
            <div className="col-12 col-lg-5 d-flex flex-column gap-3">
                <div className="card border shadow-sm">
                    <div className="card-header bg-white py-2 px-3 d-flex align-items-center justify-content-between">
                        <div className="text-muted text-uppercase fw-bold mb-0" style={{ fontSize: '0.65rem' }}>
                            {i18n.t('Product details')}
                        </div>
                        <div className="text-muted small d-flex align-items-center">
                            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                            <span>{i18n.t('Loading')}</span>
                        </div>
                    </div>
                    <div className="card-body py-0">
                        <SkeletonLine />
                        <SkeletonLine />
                        <SkeletonLine />
                    </div>
                </div>
                <div className="card">
                    <div className="card-header bg-white">
                        <div className="text-muted small text-uppercase fw-bold mb-0">
                            {i18n.t('Order total')}
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="placeholder-glow">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="placeholder rounded-pill col-4"></span>
                                <span className="placeholder rounded-pill col-3"></span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="placeholder rounded-pill col-3"></span>
                                <span className="placeholder rounded-pill col-2"></span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="placeholder rounded-pill col-3"></span>
                                <span className="placeholder rounded-pill col-4"></span>
                            </div>
                            <div className="placeholder rounded-pill col-10 mt-3"></div>
                        </div>
                    </div>
                    <div className="card-footer bg-white d-grid gap-2">
                        <button type="button" className="btn btn-outline-secondary" disabled>
                            {i18n.t('Back to shop')}
                        </button>
                    </div>
                </div>
            </div>
            <div className="col-12 col-lg-7">
                <div className="placeholder-glow mb-3">
                    <div className="placeholder col-8 mb-2" style={{ height: 28 }}></div>
                    <div className="placeholder col-11 mb-3"></div>
                    <div className="placeholder rounded-pill col-10 mb-1"></div>
                    <div className="placeholder rounded-pill col-8 mb-1"></div>
                    <div className="placeholder rounded-pill col-9"></div>
                </div>
                <div className="card border-0 shadow-sm">
                    <div className="card-body placeholder-glow">
                        <div className="placeholder col-6 mb-3"></div>
                        <div className="placeholder col-12 mb-2"></div>
                        <div className="placeholder col-10"></div>
                    </div>
                </div>
            </div>
            
        </div>
    );
}

const OrderOverview = () => {
    const [order, setOrder] = useState(null);

    const applyOrderData = useCallback((data) => {
        setOrder(data);
    }, []);

    const { isLoading, error, sendRequest: fetchOrder } = useHttp(applyOrderData);

    const loadOrder = useCallback(() => {
        fetchOrder({ url: ORDER_API, json: true });
    }, [fetchOrder]);

    useEffect(() => {
        loadOrder();
    }, [loadOrder]);

    const orderLines = useMemo(() => {
        return (
            order?.sales_order_line_objects ||
            order?.order_line_objects ||
            order?.orderlines ||
            []
        );
    }, [order]);

    const totals = useMemo(() => {
        const total =
            parseFloat(
                order?.total_amount_in_vat ??
                    order?.amount_in_vat ??
                    order?.total_amount ??
                    order?.total_price ??
                    0,
            ) || 0;
        const vat = parseFloat(order?.total_amount_vat ?? order?.amount_vat ?? 0) || 0;
        const exVat = parseFloat(order?.total_amount_ex_vat ?? 0) || 0;
        const discount = parseFloat(order?.total_discount ?? order?.discount_amount_in_vat ?? 0) || 0;
        const subtotalExVat = exVat || (vat && total ? total - vat : total);
        return { total, vat, discount, subtotalExVat };
    }, [order]);

    const hasOrderIssues =
        order?.order_errors === true ||
        orderLines.some((l) => Array.isArray(l.line_errors) && l.line_errors.length > 0);

    const { open: linesPanelOpen, toggle: toggleLinesPanel } = useOrderLinesPanelOpen();

    const deliverySnap = useMemo(
        () => (order ? addressSnapshotFromOrder(order, false) : null),
        [order],
    );
    const billingSnap = useMemo(
        () => (order ? addressSnapshotFromOrder(order, true) : null),
        [order],
    );
    const showBillingCard = useMemo(() => {
        if (!order || !deliverySnap || !billingSnap) return false;
        if (!hasBillingTextOverride(order)) return false;
        return !snapshotsEqual(deliverySnap, billingSnap);
    }, [order, deliverySnap, billingSnap]);
    const hasAddressBlock = Boolean(order && deliverySnap && snapshotHasAnyContent(deliverySnap));

    const leftColumnIntro = (
        <>
            <h4 className="mb-1">{i18n.t('Review your order')}</h4>
            <p className="text-muted small mb-3">
                {i18n.t('Check the details below before you continue to checkout.')}
            </p>
            {order && (
                <div className="small mb-3">
                    {order.order_number != null && (
                        <div>
                            <span className="text-muted">{i18n.t('Order number')}: </span>
                            <span className="fw-semibold font-monospace">{order.order_number}</span>
                        </div>
                    )}
                    {order.status != null && (
                        <div className="mt-1">
                            <span className="text-muted">{i18n.t('Status')}: </span>
                            <span className="fw-semibold">{order.status_display}</span>
                        </div>
                    )}
                    {hasAddressBlock && (
                        <div className="mt-3">
                            <div className="row g-3">
                                <div className={showBillingCard ? 'col-12 col-md-6' : 'col-12'}>
                                    <OrderAddressCard
                                        title={
                                            showBillingCard
                                                ? i18n.t('Delivery address')
                                                : i18n.t('Delivery & billing address')
                                        }
                                        snapshot={deliverySnap}
                                    />
                                </div>
                                {showBillingCard && (
                                    <div className="col-12 col-md-6">
                                        <OrderAddressCard
                                            title={i18n.t('Billing address')}
                                            snapshot={billingSnap}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );

    if (isLoading && !order) {
        return (
            <div className="container py-4">
                <OrderOverviewSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h4 className="mb-0">{i18n.t('Review your order')}</h4>
                </div>
                <div className="card border-danger-subtle">
                    <div className="card-body py-5 text-center">
                        <div className="mb-3 text-danger" style={{ fontSize: 28, lineHeight: 1 }}>
                            <Icon name="ICON_WARNING" />
                        </div>
                        <h5 className="mb-2">{i18n.t('Could not load order')}</h5>
                        <p className="text-muted mb-4">
                            {i18n.t('Please check your connection and try again')}
                        </p>
                        <a href="/cart/" style={{width: 'fit-content'}} className="btn btn-outline-secondary">
                            {i18n.t('Back to shop')}
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    const orderLinesCard = (
        <div className="card border shadow-sm">
            <div className="card-header bg-white p-0 border-bottom-0">
                <button
                    type="button"
                    className="btn btn-link text-decoration-none text-dark w-100 text-start py-2 px-3 rounded-0 d-flex align-items-center justify-content-between gap-2 border-0"
                    onClick={toggleLinesPanel}
                    aria-expanded={linesPanelOpen}
                    aria-controls="order-lines-products-panel"
                >
                    <div className="me-2 min-w-0 flex-grow-1">
                        <div
                            className="text-muted text-uppercase fw-bold mb-0"
                            style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}
                        >
                            {i18n.t('Product details')}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {orderLines.length} {orderLines.length === 1 ? i18n.t('item') : i18n.t('items')} ·{' '}
                            {currencyFormatter(totals.total)}
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        {isLoading && (
                            <div
                                className="text-muted d-flex align-items-center"
                                style={{ fontSize: '0.75rem' }}
                                aria-live="polite"
                            >
                                <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <span className="d-none d-sm-inline ms-2">{i18n.t('Loading')}</span>
                            </div>
                        )}
                        <Icon name={linesPanelOpen ? 'ICON_CHEVRON_UP' :  'ICON_CHEVRON_DOWN'} />
                    </div>
                </button>
            </div>
            <div
                id="order-lines-products-panel"
                className={`card-body py-0 px-2 px-sm-3 border-top ${linesPanelOpen ? '' : 'd-none'}`}
                role="region"
                aria-label={i18n.t('Product details')}
            >
                {orderLines.map((line) => (
                    <OrderLineRow key={line.id} line={line} />
                ))}
            </div>
        </div>
    );

    const orderTotalCard = (
        <div className="card border shadow-sm">
            <div className="card-header bg-white py-2 px-3">
                <div
                    className="text-muted text-uppercase fw-bold mb-0"
                    style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}
                >
                    {i18n.t('Order total')}
                </div>
            </div>
            <div className="card-body py-3">
                <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{i18n.t('Subtotal ex VAT')}</span>
                    <span className="fw-semibold">{currencyFormatter(totals.subtotalExVat)}</span>
                </div>
                {totals.discount ? (
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">{i18n.t('Discount')}</span>
                        <span className="text-success">-{currencyFormatter(Math.abs(totals.discount))}</span>
                    </div>
                ) : null}
                {totals.vat ? (
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">{i18n.t('VAT')}</span>
                        <span className="text-muted">{currencyFormatter(totals.vat)}</span>
                    </div>
                ) : null}
                <hr />
                <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">{i18n.t('Total incl. VAT')}</span>
                    <span className="fw-bold fs-5">{currencyFormatter(totals.total)}</span>
                </div>
                <p className="small text-muted mt-2 mb-0">
                    {i18n.t('Including VAT, excluding shipping costs')}
                </p>
            </div>
            <div className="card-footer bg-white d-grid gap-2">
                <a href="/" className="btn btn-outline-secondary">
                    {i18n.t('Back to shop')}
                </a>
            </div>
        </div>
    );

    const emptyOrderRightCard = (
        <div className="card">
            <div className="card-body py-5 text-center">
                <div className="mb-3 text-muted" style={{ fontSize: 28, lineHeight: 1 }}>
                    <Icon name="ICON_INFO" />
                </div>
                <h5 className="mb-2">{i18n.t('No lines on this order yet')}</h5>
                <p className="text-muted mb-4">
                    {i18n.t('Add products from the shop to build your order.')}
                </p>
                <a href="/" className="btn btn-primary">
                    {i18n.t('Back to shop')}
                </a>
            </div>
        </div>
    );

    return (
        <div className="container py-4">
            <div className="row g-3 g-lg-4">
                  <div className="col-12 col-lg-5 d-flex flex-column gap-3">
                    {orderLines.length === 0 ? (
                        emptyOrderRightCard
                    ) : (
                        <>
                            {orderLinesCard}
                            {orderTotalCard}
                        </>
                    )}
                </div>
                <div className="col-12 col-lg-7">
                    {leftColumnIntro}
                    {hasOrderIssues && (
                        <div className="alert alert-warning d-flex align-items-start gap-2 mb-3" role="alert">
                            <Icon name="ICON_WARNING" className="flex-shrink-0 mt-1" />
                            <div>
                                <div className="fw-semibold">{i18n.t('Action required before checkout')}</div>
                                <div className="small mb-0">
                                    {i18n.t(
                                        'One or more lines have errors (for example stock). Resolve them or adjust your order.',
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {hasOrderIssues ? (
                        <div className="alert alert-light border mb-0" role="status">
                            <div className="fw-semibold mb-1">{i18n.t('Action required before checkout')}</div>
                            <p className="small text-muted mb-3">
                                {i18n.t(
                                    'One or more lines have errors (for example stock). Resolve them or adjust your order.',
                                )}
                            </p>
                            <a href="/" className="btn btn-outline-secondary btn-sm">
                                {i18n.t('Back to shop')}
                            </a>
                        </div>
                    ) : (
                        <div className="mb-0">
                            <FormBlock url={ORDER_FORM} className="" />
                        </div>
                    )}
                </div>

              
            </div>
        </div>
    );
};

export default OrderOverview;
